import { useCallback } from 'react';
import { PermissionSetType } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { PermissionRuleName } from 'constant/permission-rules';
import { useAccountDataQuery } from 'queries/account';
import { useCurrentUserAcceLevel } from './permissions';

const AllowedRules = {
  [PermissionSetType.AccountVendorCollaborator]: [
    PermissionRuleName.TimesheetPage,
    PermissionRuleName.ProjectViewAsCustomerAction,
    PermissionRuleName.ProjectPlanPage,
    PermissionRuleName.ProjectOverviewPage,
    PermissionRuleName.ProjectFinancialsPage,
    PermissionRuleName.ProjectSpacesPage,
    PermissionRuleName.ProjectStatusUpdatePage,
    PermissionRuleName.ProjectFormResponsePage,
    PermissionRuleName.ProjectHoursSection,
    PermissionRuleName.ProjectPlannedVsActualSection,
    PermissionRuleName.ProjectGoalsSection,
    PermissionRuleName.ProjectFieldsInOverviewSection,
    PermissionRuleName.ProjectNativeMoneyFieldSection,
    PermissionRuleName.ProjectCustomFieldSection,
    PermissionRuleName.ProjectPhaseCreateAction,
    PermissionRuleName.ProjectPhaseEditAction,
    PermissionRuleName.ProjectPhaseAddTasksAction,
    PermissionRuleName.ProjectExportPrivateResourceAction,
    PermissionRuleName.ProjectPhaseRearrangeTasksAction,
    PermissionRuleName.StatusUpdateCreateAction,
    PermissionRuleName.StatusUpdateCreatePrivateAction,
    PermissionRuleName.FileGroupCreateAction,
    PermissionRuleName.FilePrivateGroupCreateAction,
    PermissionRuleName.FileUploadAction,
    PermissionRuleName.FormResponseEditAction,
    PermissionRuleName.FormResponseDeleteAction,
    PermissionRuleName.ProjectChatCreateAction,
    PermissionRuleName.ProjectChatPrivateCreateAction,
    PermissionRuleName.ProjectChatDeleteAction,
    PermissionRuleName.ProjectChatEditAction,
    PermissionRuleName.SpaceSharedCreateAction,
    PermissionRuleName.SpaceSharedEditAction,
    PermissionRuleName.SpaceSharedDeleteAction,
    PermissionRuleName.SpacePrivateSection,
    PermissionRuleName.SpaceSharedDocumentCreateAction,
    PermissionRuleName.SpaceSharedDocumentDeleteAction,
    PermissionRuleName.SpaceSharedDocumentRearrangeAction,
    PermissionRuleName.SpaceDocumentFreezeAction,
    PermissionRuleName.CommentPrivateMessageCreateAction,
    PermissionRuleName.ApprovalPrivateCreateAction,
    PermissionRuleName.SpaceDocumentInsertPrivateSectionAction,
    PermissionRuleName.TaskCreateAction,
    PermissionRuleName.TaskDeleteAction,
    PermissionRuleName.TaskNameEditAction,
    PermissionRuleName.TaskChangePrivacyAction,
    PermissionRuleName.TaskDuplicateAction,
    PermissionRuleName.TaskPrivateDescriptionSection,
    PermissionRuleName.TaskAddDependentTaskAction,
    PermissionRuleName.TaskRemoveDependentTaskAction,
    PermissionRuleName.TaskMoveBetweenPhaseAction,
    PermissionRuleName.TaskCsatSection,
    PermissionRuleName.ProjectSettingDetailPage,
    PermissionRuleName.ProjectSettingVisibilityPage,
    PermissionRuleName.TaskCreateJiraIssueAction,
  ],
  [PermissionSetType.SuperUser]: [
    PermissionRuleName.RoleSettingPage,
    PermissionRuleName.TeamMemberSettingPage,
    PermissionRuleName.DashboardPage,
    PermissionRuleName.ReportsPage,
    PermissionRuleName.ResourceManagementPage,
    PermissionRuleName.TimesheetPage,
    PermissionRuleName.TemplatePage,
    PermissionRuleName.ProjectCreateAction,
    PermissionRuleName.ProjectViewAsCustomerAction,
    PermissionRuleName.TaskCreateAction,
    PermissionRuleName.ProjectChangeTabVisibilityAction,
    PermissionRuleName.ProjectPlanPage,
    PermissionRuleName.ProjectOverviewPage,
    PermissionRuleName.ProjectFinancialsPage,
    PermissionRuleName.ProjectSpacesPage,
    PermissionRuleName.ProjectStatusUpdatePage,
    PermissionRuleName.ProjectFormResponsePage,
    PermissionRuleName.ProjectHoursSection,
    PermissionRuleName.ProjectPlannedVsActualSection,
    PermissionRuleName.ProjectGoalsSection,
    PermissionRuleName.ProjectFieldsInOverviewSection,
    PermissionRuleName.ProjectNativeMoneyFieldSection,
    PermissionRuleName.ProjectCustomFieldSection,
    PermissionRuleName.ProjectPhaseCreateAction,
    PermissionRuleName.ProjectPhaseEditAction,
    PermissionRuleName.ProjectPhaseAddTasksAction,
    PermissionRuleName.ProjectExportPrivateResourceAction,
    PermissionRuleName.ProjectPhaseRearrangeTasksAction,
    PermissionRuleName.StatusUpdateCreateAction,
    PermissionRuleName.StatusUpdateCreatePrivateAction,
    PermissionRuleName.FileGroupCreateAction,
    PermissionRuleName.FilePrivateGroupCreateAction,
    PermissionRuleName.FileUploadAction,
    PermissionRuleName.FormResponseEditAction,
    PermissionRuleName.FormResponseDeleteAction,
    PermissionRuleName.ProjectChatCreateAction,
    PermissionRuleName.ProjectChatPrivateCreateAction,
    PermissionRuleName.ProjectChatDeleteAction,
    PermissionRuleName.ProjectChatEditAction,
    PermissionRuleName.SpaceSharedCreateAction,
    PermissionRuleName.SpaceSharedEditAction,
    PermissionRuleName.SpaceSharedDeleteAction,
    PermissionRuleName.SpacePrivateSection,
    PermissionRuleName.SpaceSharedDocumentCreateAction,
    PermissionRuleName.SpaceSharedDocumentDeleteAction,
    PermissionRuleName.SpaceSharedDocumentRearrangeAction,
    PermissionRuleName.SpaceDocumentFreezeAction,
    PermissionRuleName.CommentPrivateMessageCreateAction,
    PermissionRuleName.ApprovalPrivateCreateAction,
    PermissionRuleName.SpaceDocumentInsertPrivateSectionAction,
    PermissionRuleName.TaskDeleteAction,
    PermissionRuleName.TaskNameEditAction,
    PermissionRuleName.TaskChangePrivacyAction,
    PermissionRuleName.TaskDuplicateAction,
    PermissionRuleName.TaskMoveBetweenPhaseAction,
    PermissionRuleName.TaskPrivateDescriptionSection,
    PermissionRuleName.TaskAddDependentTaskAction,
    PermissionRuleName.TaskRemoveDependentTaskAction,
    PermissionRuleName.TaskCsatSection,
    PermissionRuleName.ProjectSettingDetailPage,
    PermissionRuleName.ProjectSettingVisibilityPage,
    PermissionRuleName.TaskCreateJiraIssueAction,
  ],
  [PermissionSetType.AccountCustomerCollaborator]: [
    PermissionRuleName.TaskCreateAction,
    PermissionRuleName.ProjectGoalsSection,
    PermissionRuleName.FileGroupCreateAction,
    PermissionRuleName.FileUploadAction,
    PermissionRuleName.FormResponseEditAction,
    PermissionRuleName.FormResponseDeleteAction,
    PermissionRuleName.ProjectChatCreateAction,
    PermissionRuleName.ProjectChatDeleteAction,
    PermissionRuleName.ProjectChatEditAction,
    PermissionRuleName.TaskMoveBetweenPhaseAction,
    PermissionRuleName.SpaceSharedDocumentCreateAction,
    PermissionRuleName.SpaceSharedDocumentDeleteAction,
    PermissionRuleName.CommentPrivateMessageCreateAction,
    PermissionRuleName.ApprovalPrivateCreateAction,
    PermissionRuleName.TaskDeleteAction,
    PermissionRuleName.TaskNameEditAction,
    PermissionRuleName.ProjectSettingDetailPage,
    PermissionRuleName.TaskAddDependentTaskAction,
    PermissionRuleName.TaskRemoveDependentTaskAction,
    PermissionRuleName.TaskCreateJiraIssueAction,
    PermissionRuleName.ProjectHoursSection,
  ],
  [PermissionSetType.PartnerAdmin]: [
    PermissionRuleName.TeamMemberSettingPage,
    PermissionRuleName.OrganisationSettingsPage,
    PermissionRuleName.TaskCreateAction,
    PermissionRuleName.ProjectPlanPage,
    PermissionRuleName.ProjectViewAsCustomerAction,
    PermissionRuleName.ProjectOverviewPage,
    PermissionRuleName.ProjectSpacesPage,
    PermissionRuleName.ProjectStatusUpdatePage,
    PermissionRuleName.ProjectFormResponsePage,
    PermissionRuleName.ProjectPlannedVsActualSection,
    PermissionRuleName.ProjectGoalsSection,
    PermissionRuleName.ProjectFieldsInOverviewSection,
    PermissionRuleName.ProjectCustomFieldSection,
    PermissionRuleName.ProjectPhaseCreateAction,
    PermissionRuleName.ProjectPhaseEditAction,
    PermissionRuleName.ProjectPhaseAddTasksAction,
    PermissionRuleName.ProjectExportPrivateResourceAction,
    PermissionRuleName.ProjectPhaseRearrangeTasksAction,
    PermissionRuleName.StatusUpdateCreateAction,
    PermissionRuleName.StatusUpdateCreatePrivateAction,
    PermissionRuleName.FileGroupCreateAction,
    PermissionRuleName.FilePrivateGroupCreateAction,
    PermissionRuleName.FileUploadAction,
    PermissionRuleName.FormResponseEditAction,
    PermissionRuleName.FormResponseDeleteAction,
    PermissionRuleName.ProjectChatCreateAction,
    PermissionRuleName.ProjectChatPrivateCreateAction,
    PermissionRuleName.ProjectChatEditAction,
    PermissionRuleName.SpaceSharedCreateAction,
    PermissionRuleName.SpaceSharedEditAction,
    PermissionRuleName.SpaceSharedDeleteAction,
    PermissionRuleName.SpacePrivateSection,
    PermissionRuleName.SpaceSharedDocumentCreateAction,
    PermissionRuleName.SpaceSharedDocumentDeleteAction,
    PermissionRuleName.SpaceSharedDocumentRearrangeAction,
    PermissionRuleName.SpaceDocumentFreezeAction,
    PermissionRuleName.CommentPrivateMessageCreateAction,
    PermissionRuleName.ApprovalPrivateCreateAction,
    PermissionRuleName.SpaceDocumentInsertPrivateSectionAction,
    PermissionRuleName.TaskDeleteAction,
    PermissionRuleName.TaskNameEditAction,
    PermissionRuleName.TaskChangePrivacyAction,
    PermissionRuleName.TaskDuplicateAction,
    PermissionRuleName.TaskMoveBetweenPhaseAction,
    PermissionRuleName.TaskPrivateDescriptionSection,
    PermissionRuleName.TaskAddDependentTaskAction,
    PermissionRuleName.TaskRemoveDependentTaskAction,
    PermissionRuleName.TaskCsatSection,
  ],
  [PermissionSetType.PartnerMember]: [
    PermissionRuleName.TaskCreateAction,
    PermissionRuleName.ProjectPlanPage,
    PermissionRuleName.ProjectViewAsCustomerAction,
    PermissionRuleName.ProjectOverviewPage,
    PermissionRuleName.ProjectSpacesPage,
    PermissionRuleName.ProjectStatusUpdatePage,
    PermissionRuleName.ProjectFormResponsePage,
    PermissionRuleName.ProjectPlannedVsActualSection,
    PermissionRuleName.ProjectGoalsSection,
    PermissionRuleName.ProjectFieldsInOverviewSection,
    PermissionRuleName.ProjectCustomFieldSection,
    PermissionRuleName.ProjectPhaseCreateAction,
    PermissionRuleName.ProjectPhaseEditAction,
    PermissionRuleName.ProjectPhaseAddTasksAction,
    PermissionRuleName.ProjectExportPrivateResourceAction,
    PermissionRuleName.ProjectPhaseRearrangeTasksAction,
    PermissionRuleName.StatusUpdateCreateAction,
    PermissionRuleName.StatusUpdateCreatePrivateAction,
    PermissionRuleName.FileGroupCreateAction,
    PermissionRuleName.FilePrivateGroupCreateAction,
    PermissionRuleName.FileUploadAction,
    PermissionRuleName.FormResponseEditAction,
    PermissionRuleName.FormResponseDeleteAction,
    PermissionRuleName.ProjectChatCreateAction,
    PermissionRuleName.ProjectChatPrivateCreateAction,
    PermissionRuleName.ProjectChatEditAction,
    PermissionRuleName.SpaceSharedCreateAction,
    PermissionRuleName.SpaceSharedEditAction,
    PermissionRuleName.SpaceSharedDeleteAction,
    PermissionRuleName.SpacePrivateSection,
    PermissionRuleName.SpaceSharedDocumentCreateAction,
    PermissionRuleName.SpaceSharedDocumentDeleteAction,
    PermissionRuleName.SpaceSharedDocumentRearrangeAction,
    PermissionRuleName.SpaceDocumentFreezeAction,
    PermissionRuleName.CommentPrivateMessageCreateAction,
    PermissionRuleName.ApprovalPrivateCreateAction,
    PermissionRuleName.SpaceDocumentInsertPrivateSectionAction,
    PermissionRuleName.TaskDeleteAction,
    PermissionRuleName.TaskNameEditAction,
    PermissionRuleName.TaskChangePrivacyAction,
    PermissionRuleName.TaskDuplicateAction,
    PermissionRuleName.TaskMoveBetweenPhaseAction,
    PermissionRuleName.TaskPrivateDescriptionSection,
    PermissionRuleName.TaskAddDependentTaskAction,
    PermissionRuleName.TaskRemoveDependentTaskAction,
    PermissionRuleName.TaskCsatSection,
  ],
};
const AccessLevelSpecificRules = [
  PermissionRuleName.TaskCreateAction,
  PermissionRuleName.ProjectPlanPage,
  PermissionRuleName.ProjectViewAsCustomerAction,
  PermissionRuleName.ProjectOverviewPage,
  PermissionRuleName.ProjectSpacesPage,
  PermissionRuleName.ProjectStatusUpdatePage,
  PermissionRuleName.ProjectFormResponsePage,
  PermissionRuleName.ProjectGoalsSection,
  PermissionRuleName.ProjectFieldsInOverviewSection,
  PermissionRuleName.ProjectCustomFieldSection,
  PermissionRuleName.ProjectPhaseCreateAction,
  PermissionRuleName.ProjectPhaseEditAction,
  PermissionRuleName.ProjectPhaseAddTasksAction,
  PermissionRuleName.ProjectExportPrivateResourceAction,
  PermissionRuleName.ProjectPhaseRearrangeTasksAction,
  PermissionRuleName.StatusUpdateCreateAction,
  PermissionRuleName.StatusUpdateCreatePrivateAction,
  PermissionRuleName.FileGroupCreateAction,
  PermissionRuleName.FilePrivateGroupCreateAction,
  PermissionRuleName.FileUploadAction,
  PermissionRuleName.FormResponseEditAction,
  PermissionRuleName.FormResponseDeleteAction,
  PermissionRuleName.ProjectChatCreateAction,
  PermissionRuleName.ProjectChatPrivateCreateAction,
  PermissionRuleName.ProjectChatEditAction,
  PermissionRuleName.SpaceSharedCreateAction,
  PermissionRuleName.SpaceSharedEditAction,
  PermissionRuleName.SpaceSharedDeleteAction,
  PermissionRuleName.SpacePrivateSection,
  PermissionRuleName.SpaceSharedDocumentCreateAction,
  PermissionRuleName.SpaceSharedDocumentDeleteAction,
  PermissionRuleName.SpaceSharedDocumentRearrangeAction,
  PermissionRuleName.SpaceDocumentFreezeAction,
  PermissionRuleName.CommentPrivateMessageCreateAction,
  PermissionRuleName.ApprovalPrivateCreateAction,
  PermissionRuleName.SpaceDocumentInsertPrivateSectionAction,
  PermissionRuleName.TaskDeleteAction,
  PermissionRuleName.TaskNameEditAction,
  PermissionRuleName.TaskChangePrivacyAction,
  PermissionRuleName.TaskDuplicateAction,
  PermissionRuleName.TaskMoveBetweenPhaseAction,
  PermissionRuleName.TaskPrivateDescriptionSection,
  PermissionRuleName.TaskAddDependentTaskAction,
  PermissionRuleName.TaskRemoveDependentTaskAction,
  PermissionRuleName.TaskCsatSection,
];

const AccessLevelWiseAllowedRules: Record<
  PartnerPermissionName,
  PermissionRuleName[]
> = {
  [PartnerPermissionName.Permissive]: [
    PermissionRuleName.TaskCreateAction,
    PermissionRuleName.ProjectPlanPage,
    PermissionRuleName.ProjectViewAsCustomerAction,
    PermissionRuleName.ProjectOverviewPage,
    PermissionRuleName.ProjectSpacesPage,
    PermissionRuleName.ProjectStatusUpdatePage,
    PermissionRuleName.ProjectFormResponsePage,
    PermissionRuleName.ProjectGoalsSection,
    PermissionRuleName.ProjectFieldsInOverviewSection,
    PermissionRuleName.ProjectCustomFieldSection,
    PermissionRuleName.ProjectPhaseCreateAction,
    PermissionRuleName.ProjectPhaseEditAction,
    PermissionRuleName.ProjectPhaseAddTasksAction,
    PermissionRuleName.ProjectExportPrivateResourceAction,
    PermissionRuleName.ProjectPhaseRearrangeTasksAction,
    PermissionRuleName.StatusUpdateCreateAction,
    PermissionRuleName.StatusUpdateCreatePrivateAction,
    PermissionRuleName.FileGroupCreateAction,
    PermissionRuleName.FilePrivateGroupCreateAction,
    PermissionRuleName.FileUploadAction,
    PermissionRuleName.FormResponseEditAction,
    PermissionRuleName.FormResponseDeleteAction,
    PermissionRuleName.ProjectChatCreateAction,
    PermissionRuleName.ProjectChatPrivateCreateAction,
    PermissionRuleName.ProjectChatEditAction,
    PermissionRuleName.SpaceSharedCreateAction,
    PermissionRuleName.SpaceSharedEditAction,
    PermissionRuleName.SpaceSharedDeleteAction,
    PermissionRuleName.SpacePrivateSection,
    PermissionRuleName.SpaceSharedDocumentCreateAction,
    PermissionRuleName.SpaceSharedDocumentDeleteAction,
    PermissionRuleName.SpaceSharedDocumentRearrangeAction,
    PermissionRuleName.SpaceDocumentFreezeAction,
    PermissionRuleName.CommentPrivateMessageCreateAction,
    PermissionRuleName.ApprovalPrivateCreateAction,
    PermissionRuleName.SpaceDocumentInsertPrivateSectionAction,
    PermissionRuleName.TaskDeleteAction,
    PermissionRuleName.TaskNameEditAction,
    PermissionRuleName.TaskChangePrivacyAction,
    PermissionRuleName.TaskDuplicateAction,
    PermissionRuleName.TaskMoveBetweenPhaseAction,
    PermissionRuleName.TaskPrivateDescriptionSection,
    PermissionRuleName.TaskAddDependentTaskAction,
    PermissionRuleName.TaskRemoveDependentTaskAction,
    PermissionRuleName.TaskCsatSection,
  ],
  [PartnerPermissionName.Moderate]: [
    PermissionRuleName.TaskCreateAction,
    PermissionRuleName.ProjectPlanPage,
    PermissionRuleName.ProjectViewAsCustomerAction,
    PermissionRuleName.ProjectOverviewPage,
    PermissionRuleName.ProjectSpacesPage,
    PermissionRuleName.ProjectStatusUpdatePage,
    PermissionRuleName.ProjectFormResponsePage,
    PermissionRuleName.ProjectGoalsSection,
    PermissionRuleName.StatusUpdateCreateAction,
    PermissionRuleName.FileGroupCreateAction,
    PermissionRuleName.FileUploadAction,
    PermissionRuleName.ProjectChatCreateAction,
    PermissionRuleName.ProjectChatEditAction,
    PermissionRuleName.SpaceSharedCreateAction,
    PermissionRuleName.SpaceSharedEditAction,
    PermissionRuleName.SpaceSharedDeleteAction,
    PermissionRuleName.SpaceSharedDocumentCreateAction,
    PermissionRuleName.SpaceSharedDocumentDeleteAction,
    PermissionRuleName.SpaceSharedDocumentRearrangeAction,
    PermissionRuleName.SpaceDocumentFreezeAction,
    PermissionRuleName.TaskDeleteAction,
    PermissionRuleName.TaskNameEditAction,
    PermissionRuleName.TaskDuplicateAction,
    PermissionRuleName.TaskAddDependentTaskAction,
    PermissionRuleName.TaskRemoveDependentTaskAction,
  ],
  [PartnerPermissionName.Restricted]: [
    PermissionRuleName.TaskAddDependentTaskAction,
    PermissionRuleName.ProjectPlanPage,
  ],
};

interface ReturnValue {
  isAllowedForCurrentUser: (rule: PermissionRuleName) => boolean;
}
export const usePermissionForRule = (): ReturnValue => {
  const { data } = useAccountDataQuery();
  const currentUserPermissionSetType =
    data?.userProfile.permissions.permissionSet.permissionSetType;
  const accessLevel = useCurrentUserAcceLevel();

  const isAllowedForCurrentUser = useCallback(
    (rule: PermissionRuleName) => {
      if (currentUserPermissionSetType == null) {
        return false;
      }

      return (
        AllowedRules[currentUserPermissionSetType] == null ||
        (AllowedRules[currentUserPermissionSetType].includes(rule) &&
          (accessLevel != null && AccessLevelSpecificRules.includes(rule)
            ? AccessLevelWiseAllowedRules[accessLevel].includes(rule)
            : true))
      );
    },
    [accessLevel, currentUserPermissionSetType],
  );

  return {
    isAllowedForCurrentUser,
  };
};
